import type { SupportedLocale } from '@abaninja/types';

const ABACUS_BASE_URL = 'https://www.abacus.ch/';
const DEEP_CLOUD_BASE_URL = 'https://www.deepcloud.swiss/';
const ABANINJA_BASE_URL = 'https://www.abaninja.ch/';
const ABANINJA_HELPDESK_BASE_URL = 'https://help.abaninja.ch';
const DEEP_SIGN_BASE_URL = 'https://www.deepsign.swiss/';
const HOOP_BASE_URL = 'https://www.hoop.swiss/';
const S21_BASE_URL = 'https://swiss21.org/';
const S21_SUPPORT_BASE_URL = 'https://support.swiss21.org';
const S21_HELPDESK_BASE_URL = 'https://help.swiss21.org';

const HOOP_FRONTEND_URL = import.meta.env.VITE_HOOP_FRONTEND_URL ?? '';

const DEEP_CLOUD_IMPRINT: Record<SupportedLocale, string> = {
  de: '/impressum',
  en: '/en/impressum',
  fr: '/fr/mentions-legales/',
  it: '/it/nota-legale/',
};

const DEEP_CLOUD_PRIVACY_POLICY: Record<SupportedLocale, string> = {
  de: '/datenschutz',
  en: '/en/privacy-policy/',
  fr: '/fr/politique-des-donnees/',
  it: '/it/protezione-dei-dati/',
};

const DEEP_CLOUD_API_TERMS_OF_USE: Record<SupportedLocale, string> = {
  de: '/legal/NB-DeepCloud-AG_API_DE.pdf',
  en: '/legal/NB-DeepCloud-AG_API_EN.pdf',
  fr: '/legal/NB-DeepCloud-AG_API_FR.pdf',
  it: '/legal/NB-DeepCloud-AG_API_IT.pdf',
};

const DEEP_CLOUD_TERMS_OF_USE: Record<SupportedLocale, string> = {
  de: '/legal/AB-DeepCloud-AG_DE.pdf',
  en: '/legal/AB-DeepCloud-AG_EN.pdf',
  fr: '/legal/AB-DeepCloud-AG_FR.pdf',
  it: '/legal/AB-DeepCloud-AG_IT.pdf',
};

const DEEP_CLOUD_NINJA_TERMS_OF_USE: Record<SupportedLocale, string> = {
  de: '/legal/AB-DeepCloud-AG_Ninja_DE.pdf',
  en: '/legal/AB-DeepCloud-AG_Ninja_EN.pdf',
  fr: '/legal/AB-DeepCloud-AG_Ninja_FR.pdf',
  it: '/legal/AB-DeepCloud-AG_Ninja_IT.pdf',
};

const ABA_BRIDGE_TERMS_OF_USE: Record<SupportedLocale, string> = {
  de: '/legal/2024-agb-ababridge-de.pdf',
  en: '/legal/2024-agb-ababridge-en.pdf',
  fr: '/legal/2024-agb-ababridge-fr.pdf',
  it: '/legal/2024-agb-ababridge-it.pdf',
};

const DEEP_SIGN_COST: Record<SupportedLocale, string> = {
  de: '/de/preise',
  en: '/en/pricing',
  fr: '/fr/tarifs',
  it: '/it/prezzi',
};

const HOOP_CHANGE_COMPANY: Record<SupportedLocale, string> = {
  de: '/de/firmen-aenderungen',
  en: '/en/company-changes',
  fr: '/fr/modifications-de-societes',
  it: '/it/modifiche-societarie',
};

const SWISS21_TERMS_OF_SERVICE: Record<SupportedLocale, string> = {
  de: '/agb',
  en: '/agb',
  fr: '/fr/conditions-generales',
  it: '/it/condizioni',
};

const SWISS21_PRIVACY_POLICY: Record<SupportedLocale, string> = {
  de: '/datenschutzerklarung',
  en: '/datenschutz',
  fr: '/fr/politique-de-confidentialite',
  it: '/it/politica-sulla-riservatezza',
};

const SWISS21_HELPDESK: Record<SupportedLocale, string> = {
  de: '/de',
  en: '/en',
  fr: '/fr',
  it: '/it',
};

const SWISS21_AVAILABLE_BANK_CONNECTIONS: Record<SupportedLocale, string> = {
  de: '/verfuegbare-bankanbindungen',
  en: '/verfuegbare-bankanbindungen',
  fr: '/fr/connexions-bancaires-disponibles',
  it: '/it/connessione-bancaria-disponibile',
};

const SWISS21_MODULES: Record<SupportedLocale, string> = {
  de: '/anbindungen',
  en: '/anbindungen',
  fr: '/fr/connexions',
  it: '/it/interconnessioni',
};

const SWISS21_DIGITAL_COACH: Record<SupportedLocale, string> = {
  de: '/digital-coach',
  en: '/digital-coach',
  fr: '/fr/digital-coach',
  it: '/it/digital-coach',
};

export interface HoopDebtCollectionParams {
  accountUuid: string;
  invoiceUrl: string;
  invoiceUuid: string;
  openAmount: number;
}

function hoopDebtCollectionUrl({ accountUuid, invoiceUrl, invoiceUuid, openAmount }: HoopDebtCollectionParams) {
  const url = new URL('/external', HOOP_FRONTEND_URL);

  url.search = new URLSearchParams([
    ['sender', 'abaninja'],
    ['accountUuid', accountUuid],
    ['invoiceUuid', invoiceUuid],
    ['openAmount', String(openAmount)],
    ['invoiceUrl', invoiceUrl],
  ]).toString();

  return url.href;
}

function localeUrl(
  paths: Record<SupportedLocale, string>,
  base: string,
): (params: { locale: SupportedLocale }) => string {
  return ({ locale }) => new URL(paths[locale], base).href;
}

export const externalLinks = {
  abaBridgeTermsOfUse: localeUrl(ABA_BRIDGE_TERMS_OF_USE, ABANINJA_BASE_URL),
  deepCloudApiTermsOfUse: localeUrl(DEEP_CLOUD_API_TERMS_OF_USE, DEEP_CLOUD_BASE_URL),
  deepCloudImprint: localeUrl(DEEP_CLOUD_IMPRINT, DEEP_CLOUD_BASE_URL),
  deepCloudNinjaTermsOfUse: localeUrl(DEEP_CLOUD_NINJA_TERMS_OF_USE, DEEP_CLOUD_BASE_URL),
  deepCloudPrivacyPolicy: localeUrl(DEEP_CLOUD_PRIVACY_POLICY, DEEP_CLOUD_BASE_URL),
  deepCloudTermsOfUse: localeUrl(DEEP_CLOUD_TERMS_OF_USE, DEEP_CLOUD_BASE_URL),
  deepSignPrices: localeUrl(DEEP_SIGN_COST, DEEP_SIGN_BASE_URL),
  hoopChangeCompany: localeUrl(HOOP_CHANGE_COMPANY, HOOP_BASE_URL),
  hoopDebtCollection: hoopDebtCollectionUrl,
  swiss21AvailableBankConnections: localeUrl(SWISS21_AVAILABLE_BANK_CONNECTIONS, S21_BASE_URL),
  swiss21DigitalCoach: localeUrl(SWISS21_DIGITAL_COACH, S21_BASE_URL),
  swiss21Helpdesk: localeUrl(SWISS21_HELPDESK, S21_HELPDESK_BASE_URL),
  swiss21Modules: localeUrl(SWISS21_MODULES, S21_BASE_URL),
  swiss21PrivacyPolicy: localeUrl(SWISS21_PRIVACY_POLICY, S21_BASE_URL),
  swiss21TermsOfService: localeUrl(SWISS21_TERMS_OF_SERVICE, S21_BASE_URL),
  abaClikAppStore: () => 'https://apps.apple.com/ch/app/abaclik-ai/id6505052062',
  abaClikPlayStore: () => 'https://play.google.com/store/apps/details?id=ch.abacus.android.abaclik.ai',

  abacus: () => new URL('/', ABACUS_BASE_URL).href,
  abaninja: () => new URL('/', ABANINJA_BASE_URL).href,
  abaninjaApiDocs: () => new URL('/apidocs', ABANINJA_BASE_URL).href,

  abaninjaHelpdesk: () => new URL('/', ABANINJA_HELPDESK_BASE_URL).href,
  // eslint-disable-next-line @stylistic/max-len
  paymentCardTestWiki: () => 'https://wiki.abacus.ch/spaces/SBUZD/pages/131944748/Test+Szenarien+und+ContractIds#TestSzenarienundContractIds-Kreditkarten',
  swiss21: () => new URL('/', S21_BASE_URL).href,
  swiss21Support: () => new URL('/', S21_SUPPORT_BASE_URL).href,
} as const;

export type ExternalLinks = typeof externalLinks;
export type ExternalLinkKey = keyof ExternalLinks;
